/**
 * The words a person sees for how work is shaped and how a plan runs.
 *
 * Inside the workbench the shaping stage is SEED and a plan's loop is PAUL's
 * PLAN → APPLY → UNIFY (both credited in webview-ui/src/credits.ts and the
 * README). Those names stay in code, prompts to agents and developer logs;
 * on screen it is "shaping the brief" and Plan › Do › Wrap up. Every view,
 * toast and conversation line takes its words from here, and
 * webview-ui/test/planLoopWording.test.ts with src/test/planLoopWording.test.ts
 * fail when an acronym comes back.
 *
 * Plain constants and functions only, no imports: both the host and the
 * renderer read this file. (A DOM-free renderer model that imports it is
 * loaded through `createRequire` in its webview test, as shared/ is CommonJS.)
 */
/** The three steps every plan goes through, in order, with the line their chip's tooltip shows. */
export const PLAN_LOOP_STEPS = [
    { id: "plan", label: "Plan", hint: "Objective, acceptance criteria, tasks and boundaries — approved before work starts" },
    { id: "apply", label: "Do", hint: "The team does the tasks, and each is checked against its criteria" },
    { id: "unify", label: "Wrap up", hint: "What shipped is checked against what was planned, and the plan closes" },
];
/** The last step as a noun: the conversation's heading, the summary card, a decision's source. */
export const WRAP_UP = "Wrap-up";
/** The button that asks for the last step now. */
export const WRAP_UP_NOW = "Wrap up now";
export const WRAP_UP_STEPS = [
    { id: "report", label: "Check what shipped against the plan", hint: "The operations manager (or a specialist you pick) writes a short report of what was built compared with what was planned." },
    { id: "criteria", label: "Verify the acceptance criteria", hint: "Each criterion the plan set is confirmed or marked as not met, by you or by an agent that runs the check." },
    { id: "followups", label: "Finish the follow-ups", hint: "Tasks the team added while it worked: finish them, move them to the backlog, or say they are not needed." },
    { id: "review", label: "Review the changes", hint: "Read every changed file, leave notes for the agent, then merge the work into main or open a pull request." },
    { id: "close", label: "Close the plan", hint: "The plan becomes Complete, its retrospective is filed with the decisions, and the team is free for the next plan." },
];
/** What closing does, under the last step. */
export const WRAP_UP_CLOSE_EFFECT = "Closing makes the plan Complete, files its retrospective with the decisions it made, and frees the team.";
/** The button on the card's header while the first step has not started. */
export const WRAP_UP_START = "Start the wrap-up";
/** The shaping stage, as a label. */
export const SHAPING_THE_BRIEF = "Shaping the brief";
/** What a brief is shaped as (application, workflow …), in a sentence: "2 brief types". */
export const BRIEF_TYPE = "brief type";
/** "Plan", "Do", "Wrap up" — or "Complete" once the loop is over. */
export function planLoopLabel(position) {
    return PLAN_LOOP_STEPS.find((s) => s.id === position)?.label ?? "Complete";
}
/** The loop's accessible name while it is moving: "Plan loop: Do, in progress". */
export function planLoopInProgress(position) {
    return position === "complete" ? "Plan loop: complete" : `Plan loop: ${planLoopLabel(position)}, in progress`;
}
//# sourceMappingURL=planLoop.js.map